import React, { Component } from 'react';
import { Group, Stack, Textarea, ScrollArea } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import CustomModal from '.';
import ColorButton from '../Buttons/ColorButton';
import AiAgentIcon from '../../../../../assets/images/module/general/AiAgentIcon.svg';
import RedCross from '../../../../../assets/images/module/general/RedCross.svg';
import { AI_AGENT_BASE_URL } from '../../../../../helpers/aiAgentApi';
import { isAiConsentAccepted } from '../../../../../helpers/aiConsent';
import { decoratePlaceholdersForPreview } from '../../../../../helpers/messagePlaceholders';

// Cycled while the request is in flight. Generation takes roughly 5-10 seconds, which is
// long enough that a bare spinner reads as a hang — these give the wait a shape without
// claiming to report real progress (the endpoint isn't streamed, so there is none to report).
const LOADING_STEPS = [
  'Ihre Beschreibung wird gelesen …',
  'Passende Platzhalter werden ausgewählt …',
  'Die Nachricht wird formuliert …',
  'Letzter Schliff …',
];

const LOADING_STEP_INTERVAL_MS = 2600;

// Starting points for users who open the modal with no idea what to type. Each is a
// realistic brief rather than a full message — the point is to show how little input the
// assistant needs.
const PROMPT_SUGGESTIONS_BY_TYPE = {
  RentalPayment: [
    'Freundliche Zahlungserinnerung für eine offene Miete',
    'Bestätigung über den Eingang der Mietzahlung',
    'Zweite Mahnung, sachlich und mit Zahlungsfrist',
  ],
  BillingStatement: [
    'Übermittlung der Betriebskostenabrechnung mit kurzer Erläuterung',
    'Abrechnung mit Nachzahlung, Zahlungsfrist freundlich erklären',
    'Abrechnung mit Guthaben, Auszahlung ankündigen',
  ],
  TenantSignUp: [
    'Einladung ins Mieterportal mit kurzer Erklärung der Vorteile',
    'Freundliche Erinnerung, sich im Mieterportal zu registrieren',
  ],
  General: [
    'Ankündigung einer Handwerkerarbeit im Treppenhaus',
    'Information über die jährliche Ablesung der Zählerstände',
    'Hinweis auf die Hausordnung, freundlich formuliert',
  ],
};

const getSuggestions = (templateType) =>
  PROMPT_SUGGESTIONS_BY_TYPE[templateType] || PROMPT_SUGGESTIONS_BY_TYPE.General;

const MAX_PROMPT_CHARS = 2000;

// Self-contained so the component can be dropped in without a stylesheet change; every
// selector is prefixed to keep it out of the global namespace.
const STYLES = `
.ai-msg-assistant-modal .modal-body { padding-top: 8px; }
.ai-msg-assistant-hint {
  font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 400; line-height: 1.55;
}
.ai-msg-assistant-chip {
  font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 400; color: #13135C;
  background: rgba(205, 205, 255, 0.5); border: 1px solid #DADAE5; border-radius: 4px;
  padding: 4px 8px; cursor: pointer; transition: background 120ms ease;
}
.ai-msg-assistant-chip:hover { background: #e6e6ff; }
.ai-msg-assistant-label {
  font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 500; color: #727293;
}
.ai-msg-assistant-preview {
  font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 400; color: #0E0E1D;
  background: #FFFFFF; border: 1px solid #DADAE5; border-radius: 4px; padding: 12px 14px;
  line-height: 22px;
}
.ai-msg-assistant-preview p { margin-bottom: 10px; }
.ai-msg-assistant-preview p:last-child { margin-bottom: 0; }
.ai-msg-assistant-error {
  font-family: 'Inter', sans-serif; font-size: 13px; color: #E8193C; line-height: 18px;
}

/* Loading state: three shimmering lines standing in for the message being written. */
.ai-msg-assistant-skeleton { display: flex; flex-direction: column; gap: 10px; width: 100%; }
.ai-msg-assistant-skeleton-line {
  height: 12px; border-radius: 6px;
  background: linear-gradient(90deg, #EFEFF5 0%, #DADAFF 40%, #EFEFF5 80%);
  background-size: 200% 100%;
  animation: ai-msg-assistant-shimmer 1.6s ease-in-out infinite;
}
@keyframes ai-msg-assistant-shimmer {
  0% { background-position: 150% 0; }
  100% { background-position: -50% 0; }
}
.ai-msg-assistant-pulse-icon { animation: ai-msg-assistant-pulse 1.8s ease-in-out infinite; }
@keyframes ai-msg-assistant-pulse {
  0%, 100% { transform: scale(1); opacity: 0.75; }
  50% { transform: scale(1.18); opacity: 1; }
}
.ai-msg-assistant-status {
  font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 400; color: #13135C;
  animation: ai-msg-assistant-fade 400ms ease;
}
@keyframes ai-msg-assistant-fade { from { opacity: 0; } to { opacity: 1; } }

@media (prefers-reduced-motion: reduce) {
  .ai-msg-assistant-skeleton-line, .ai-msg-assistant-pulse-icon, .ai-msg-assistant-status { animation: none; }
}
`;

/**
 * Writes a message subject and body from a few words of description, via the AI agent
 * server's POST /messageTemplate.
 *
 * The generated message is shown as a preview and only reaches the editor when the user
 * presses "Übernehmen" — applying it replaces whatever they had written, so the confirmation
 * step is what makes that safe. Refinement ("kürzer", "förmlicher") re-runs against the
 * generated draft, so a near-miss doesn't have to be re-described from scratch.
 */
export default class AiMessageAssistantModal extends Component {
  state = {
    step: 'input', // 'input' | 'loading' | 'result'
    prompt: '',
    refinePrompt: '',
    result: null,
    error: null,
    loadingStep: 0,
  };

  controller = null;
  loadingInterval = null;

  componentDidUpdate(prevProps) {
    // Every open starts a fresh briefing. Carrying the previous result over would show a
    // stale message next to a draft it no longer relates to.
    if (!prevProps.open && this.props.open) {
      this.setState({ step: 'input', prompt: '', refinePrompt: '', result: null, error: null });
    }
  }

  componentWillUnmount() {
    this.stopLoadingAnimation();
    this.abortRequest();
  }

  abortRequest = () => {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  };

  startLoadingAnimation = () => {
    this.stopLoadingAnimation();
    this.setState({ loadingStep: 0 });
    this.loadingInterval = setInterval(() => {
      // Holds on the last step rather than looping back — a second pass through the same
      // messages would suggest the work restarted.
      this.setState((prev) => ({ loadingStep: Math.min(prev.loadingStep + 1, LOADING_STEPS.length - 1) }));
    }, LOADING_STEP_INTERVAL_MS);
  };

  stopLoadingAnimation = () => {
    if (this.loadingInterval) {
      clearInterval(this.loadingInterval);
      this.loadingInterval = null;
    }
  };

  handleClose = () => {
    // Closing mid-generation abandons the request; the server aborts its OpenAI call when
    // the connection drops, so nothing keeps running behind the modal.
    this.abortRequest();
    this.stopLoadingAnimation();
    this.props.toggle();
  };

  hasConsent = () => isAiConsentAccepted(this.props.accountData);

  generate = async (mode) => {
    const {
      templateType,
      allowedPlaceholders,
      currentSubject,
      currentBody,
      profileData,
      accountData,
      propertyId,
    } = this.props;
    const { prompt, refinePrompt, result } = this.state;

    const instruction = (mode === 'refine' ? refinePrompt : prompt).trim();
    if (!instruction) return;

    this.abortRequest();
    this.controller = new AbortController();
    this.setState({ step: 'loading', error: null });
    this.startLoadingAnimation();

    try {
      const response = await fetch(`${AI_AGENT_BASE_URL}/messageTemplate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: this.controller.signal,
        body: JSON.stringify({
          encryptedToken: profileData?.auth?.encryptedToken,
          accountId: accountData?.accountId,
          propertyId,
          userId: profileData?.id,
          templateType,
          prompt: instruction.slice(0, MAX_PROMPT_CHARS),
          allowedPlaceholders,
          language: 'de',
          mode: mode === 'refine' ? 'refine' : 'create',
          // Refinement edits the message on screen. That is the generated draft once one
          // exists, and the user's own editor content on the first pass — which is what
          // makes "improve what I already wrote" work without a separate entry point.
          currentSubject: mode === 'refine' ? (result?.subject ?? currentSubject) : undefined,
          currentBody: mode === 'refine' ? (result?.body ?? currentBody) : undefined,
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        // 402 carries the credit-gate copy from the server; everything else gets a generic
        // German message, since the server's other errors aren't written for end users.
        const message = response.status === 402 && data?.error
          ? data.error
          : 'Die Nachricht konnte nicht erstellt werden. Bitte versuchen Sie es erneut.';
        this.failWith(message);
        return;
      }

      if (!data?.result?.body) {
        this.failWith('Die Nachricht konnte nicht erstellt werden. Bitte versuchen Sie es erneut.');
        return;
      }

      this.stopLoadingAnimation();
      this.setState({ step: 'result', result: data.result, refinePrompt: '', error: null });
    } catch (err) {
      // An abort is the user closing the modal or starting over, not a failure to report.
      if (err?.name === 'AbortError') return;
      this.failWith('Die Nachricht konnte nicht erstellt werden. Bitte versuchen Sie es erneut.');
    }
  };

  // Falls back to whichever screen the user can act on: the result if one is already there
  // (a failed refinement shouldn't throw away a usable draft), otherwise the input.
  failWith = (message) => {
    this.stopLoadingAnimation();
    this.setState((prev) => ({ step: prev.result ? 'result' : 'input', error: message }));
    showNotification({
      message,
      icon: <img src={RedCross} alt="red-cross" />,
      id: 'ai-message-assistant-error',
    });
  };

  applyResult = () => {
    const { result } = this.state;
    if (!result) return;
    this.props.onApply({ subject: result.subject, body: result.body });
    this.handleClose();
  };

  renderConsentNotice = () => (
    <Stack gap={12}>
      <span className="ai-msg-assistant-hint">
        Um die KI-Funktionen von hellohousing zu nutzen, müssen Sie den KI-Nutzungsbedingungen
        einmalig zustimmen. Öffnen Sie dazu den hellohousing KI-Assistenten und bestätigen Sie
        die Bedingungen dort.
      </span>
      <Group justify="flex-end">
        <div>
          <ColorButton text={'Schließen'} className="gray-btn" onClick={this.handleClose} />
        </div>
      </Group>
    </Stack>
  );

  renderInputStep = () => {
    const { templateType } = this.props;
    const { prompt, error } = this.state;

    return (
      <Stack gap={14}>
        <span className="ai-msg-assistant-hint">
          Beschreiben Sie in wenigen Worten, worum es in Ihrer Nachricht gehen soll. Die KI
          formuliert Betreff und Nachrichtentext auf Deutsch und setzt passende Platzhalter ein.
        </span>

        <Textarea
          autoFocus
          classNames={{ input: 'ai-msg-assistant-refine-input' }}
          value={prompt}
          onChange={(event) => this.setState({ prompt: event.currentTarget.value })}
          onKeyDown={(event) => {
            // Enter sends, Shift+Enter breaks the line — the same convention as the chat.
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              this.generate('create');
            }
          }}
          placeholder="z. B. Freundliche Erinnerung an die ausstehende Miete"
          minRows={4}
          maxRows={8}
          autosize
          maxLength={MAX_PROMPT_CHARS}
        />

        <Stack gap={6}>
          <span className="ai-msg-assistant-label">Beispiele</span>
          <Group gap={6}>
            {getSuggestions(templateType).map((suggestion) => (
              <span
                key={suggestion}
                className="ai-msg-assistant-chip"
                onClick={() => this.setState({ prompt: suggestion })}>
                {suggestion}
              </span>
            ))}
          </Group>
        </Stack>

        {error && <span className="ai-msg-assistant-error">{error}</span>}

        <Group justify="space-between">
          <div>
            <ColorButton text={'Abbrechen'} className="gray-btn" onClick={this.handleClose} />
          </div>
          <div>
            <ColorButton
            text={'Weiter'}
            className="purple-btn"
            disabled={!prompt.trim()}
            onClick={() => this.generate('create')}
            />
          </div>
        </Group>
      </Stack>
    );
  };

  renderLoadingStep = () => (
    <Stack gap={20} align="center" style={{ padding: '18px 0 8px' }}>
      <img
        src={AiAgentIcon}
        alt="ai-icon"
        className="ai-msg-assistant-pulse-icon"
        style={{ width: '34px' }}
      />
      <span className="ai-msg-assistant-status" key={this.state.loadingStep}>
        {LOADING_STEPS[this.state.loadingStep]}
      </span>
      <div className="ai-msg-assistant-skeleton">
        <div className="ai-msg-assistant-skeleton-line" style={{ width: '45%' }} />
        <div className="ai-msg-assistant-skeleton-line" style={{ width: '100%' }} />
        <div className="ai-msg-assistant-skeleton-line" style={{ width: '92%' }} />
        <div className="ai-msg-assistant-skeleton-line" style={{ width: '68%' }} />
      </div>
      <Group justify="flex-start" style={{ width: '100%' }}>
        <div>
          <ColorButton text={'Abbrechen'} className="gray-btn" onClick={this.handleClose} />
        </div>
      </Group>
    </Stack>
  );

  renderResultStep = () => {
    const { result, refinePrompt, error } = this.state;

    return (
      <Stack gap={14}>
        <Stack gap={6}>
          <span className="ai-msg-assistant-label">Betreff</span>
          <div
            className="ai-msg-assistant-preview"
            dangerouslySetInnerHTML={{ __html: decoratePlaceholdersForPreview(result?.subject) }}
          />
        </Stack>

        <Stack gap={6}>
          <span className="ai-msg-assistant-label">Nachricht</span>
          <ScrollArea.Autosize mah={280}>
            <div
              className="ai-msg-assistant-preview"
              dangerouslySetInnerHTML={{ __html: decoratePlaceholdersForPreview(result?.body) }}
            />
          </ScrollArea.Autosize>
        </Stack>

        <Stack gap={6}>
          <span className="ai-msg-assistant-label">Etwas anpassen?</span>
          <Group gap={8} wrap="nowrap" align="flex-end">
            <Textarea
              style={{ flex: 1 }}
              classNames={{ input: 'ai-msg-assistant-refine-input' }}
              value={refinePrompt}
              onChange={(event) => this.setState({ refinePrompt: event.currentTarget.value })}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  this.generate('refine');
                }
              }}
              placeholder="z. B. kürzer, förmlicher, mit Zahlungsfrist"
              minRows={1}
              maxRows={4}
              autosize
              maxLength={MAX_PROMPT_CHARS}
            />
            <div>
              <ColorButton
              text={'Anpassen'}
              className="gray-btn"
              disabled={!refinePrompt.trim()}
              onClick={() => this.generate('refine')}
              />
            </div>
          </Group>
        </Stack>

        <span className="ai-msg-assistant-hint">
          Mit „Übernehmen“ ersetzen Sie Betreff und Nachrichtentext im Editor.
        </span>

        {error && <span className="ai-msg-assistant-error">{error}</span>}

        <Group justify="space-between">
          <div>
              <ColorButton
                text={'Neu beschreiben'}
                className="gray-btn"
                onClick={() => this.setState({ step: 'input', error: null })}
              />
          </div>
          <div>
              <ColorButton text={'Übernehmen'} className="purple-btn" onClick={this.applyResult} />
          </div>
        </Group>
      </Stack>
    );
  };

  render() {
    const { open } = this.props;
    const { step } = this.state;

    return (
      <CustomModal
        heading={
          // Inline elements only: CustomModal renders the heading inside a <span>.
          <>
            <img src={AiAgentIcon} alt="ai-icon" style={{ width: '16px', marginRight: '8px', verticalAlign: '-2px' }} />
            Nachricht mit KI erstellen
          </>
        }
        open={open}
        toggle={this.handleClose}
        className="ai-msg-assistant-modal">
        <style>{STYLES}</style>
        {!this.hasConsent()
          ? this.renderConsentNotice()
          : step === 'loading'
          ? this.renderLoadingStep()
          : step === 'result'
          ? this.renderResultStep()
          : this.renderInputStep()}
      </CustomModal>
    );
  }
}
